import { createSlice } from "@reduxjs/toolkit";
import {
  deleteCollectionBuilder,
  hideCollectionBuilder,
  hideCollectionVipBuilder,
  generateCollectionBuilder,
  getCategoriesBuilder,
  getCollectionsBuilder,
  getExploreCollectionsBuilder,
  getUserFavoritesBuilder,
  toggleToFavoriteBuilder,
  getTrainModelsBuilder,
  getTrainModelBuilder,
  createTrainModelBuilder,
  updateTrainModelBuilder,
  deleteTrainModelBuilder,
  getTopCreatorsBuilder,
  regenerateCollectionBuilder,
  rateModelBuilder,
  getDefaultGalleryBuilder,
  selectCoverForTrainModelBuilder,
  setCollectionPublicCoverBuilder,
  submitTrainModelForReviewBuilder,
  getCollectionFoldersBuilder,
  createCollectionFolderBuilder,
  putCollectionToFolderBuilder,
  deleteCollectionFolderBuilder,
  generateImageCollectionBuilder,
  regenerateImageCollectionBuilder,
  enchantPromptBuilder,
  editCollectionFolderBuilder,
  generateAnonymizeBuilder,
  getAnonymizedCollectionsBuilder,
  selectCollectionsInCollectionGroupBuilder,
  fetchCollectionBuilder,
} from "./builder";
import { LOGOUT } from "../../../utils/constants";

const initialState = {
  categories: {
    data: [],
    loading: false,
    error: null,
  },

  generateCollection: {
    data: [],
    loading: false,
    error: null,
  },

  collections: {
    data: null,
    total: 0,
    perPage: 10,
    page: 0,
    maxReached: false,
    loading: false,
    error: null,
    hasDataBeenFetched: false,
  },
  anonymizedCollections: {
    data: null,
    loading: false,
  },

  gallery: {
    data: null,
  },

  favoriteCollections: {
    data: null,
    total: 0,
    perPage: 10,
    page: 0,
    maxReached: false,
    loading: false,
    error: null,
    hasDataBeenFetched: false,
  },
  exploreCollections: {
    data: null,
    maxReached: false,
    total: 0,
    perPage: 10,
    page: 0,
    loading: false,
    error: null,
    hasDataBeenFetched: false,
    filter: null,
    search: "",
    keyword: null,
  },
  selectedCollection: null,
  collectionFolders: {
    data: [],
    loading: false,
  },
  trainModels: {
    data: [],
    page: 1,
    total: null,
    loading: false,
    error: null,
    hasDataBeenFetched: false,
  },

  selectedTrainModel: {
    data: null,
    loading: true,
    error: null,
    trainModelId: null,
  },

  collectionDetails: {
    data: null,
    loading: false,
    error: null,
  },

  createTrainModel: {
    loading: false,
    error: null,
  },

  updateTrainModel: {
    loading: false,
    error: null,
  },

  topCreators: [],

  toggleToFavoritePending: false,
  deleteCollectionPending: false,
  hideCollectionPending: false,
  hideCollectionVipPending: false,
  deleteTrainModelPending: false,
  selectCoverForTrainModelPending: false,
  setCollectionPublicCoverPending: false,
  submitTrainModelForReviewPending: false,
  regenerateCollectionPending: false,
  regenerateImageCollectionPending: false,
  generateImageCollectionPending: false,
  enchantPromptPending: false,
  getTopCreatorsPending: true,
  rateModelPending: false,
  getDefaultGalleryPending: false,
  createFolderPending: false,
  deleteFolderPending: false,
  putCollectionToFolderPending: false,
  editFolderPending: false,
  generateAnonymizedCollectionPending: false,
  selectCollectionsInCollectionGroupPending: false,
};

export const modelSlice = createSlice({
  name: "modelInfo",
  initialState,
  reducers: {
    clearCollections: (state) => {
      state.collections.data = [];
      state.collections.page = 0;
      state.collections.maxReached = false;
      state.collections.loading = false;
      state.collections.error = null;
      state.collections.hasDataBeenFetched = false;
    },
    clearExploreCollections: (state) => {
      state.exploreCollections.data = null;
      state.exploreCollections.page = 0;
      state.exploreCollections.maxReached = false;
      state.exploreCollections.loading = false;
      state.exploreCollections.error = null;
      state.exploreCollections.hasDataBeenFetched = false;
    },
    // Dodajte reset akcije za kada se menja kategorija/tab
    resetCollectionsPagination: (state) => {
      state.collections.data = [];
      state.collections.page = 0;
      state.collections.maxReached = false;
    },
    resetExploreCollectionsPagination: (state) => {
      state.exploreCollections.data = [];
      state.exploreCollections.page = 0;
      state.exploreCollections.maxReached = false;
    },

    setSelectedCollection: (state, { payload }) => {
      state.selectedCollection = payload;
    },
    clearTrainModels: (state) => {
      state.trainModels.data = [];
      state.trainModels.loading = false;
      state.trainModels.error = null;
      state.trainModels.hasDataBeenFetched = false;
    },
    setSelectedTrainModel: (state, { payload }) => {
      state.selectedTrainModel.data = payload;
    },
    clearSelectedTrainModel: (state) => {
      state.selectedTrainModel.data = null;
      state.selectedTrainModel.loading = false;
      state.selectedTrainModel.error = null;
    },
    setSelectedExploreCollectionFilter: (state, { payload }) => {
      state.exploreCollections.filter = payload;
    },
    setSelectedExploreCollectionSearch: (state, { payload }) => {
      state.exploreCollections.search = payload;
    },
    setSelectedExploreCollectionKeyword: (state, { payload }) => {
      state.exploreCollections.keyword = payload;
    },
    setTrainingPage: (state, { payload }) => {
      state.trainModels.page = payload;
    },
  },
  extraReducers: (builder) => {
    getCategoriesBuilder(builder);
    getCollectionsBuilder(builder);
    getExploreCollectionsBuilder(builder);
    generateCollectionBuilder(builder);
    generateImageCollectionBuilder(builder);
    enchantPromptBuilder(builder);
    toggleToFavoriteBuilder(builder);
    getUserFavoritesBuilder(builder);
    deleteCollectionBuilder(builder);
    hideCollectionBuilder(builder);
    hideCollectionVipBuilder(builder);
    getTrainModelsBuilder(builder);
    getTrainModelBuilder(builder);
    createTrainModelBuilder(builder);
    updateTrainModelBuilder(builder);
    deleteTrainModelBuilder(builder);
    getTopCreatorsBuilder(builder);
    rateModelBuilder(builder);
    regenerateCollectionBuilder(builder);
    regenerateImageCollectionBuilder(builder);
    getDefaultGalleryBuilder(builder);
    selectCoverForTrainModelBuilder(builder);
    setCollectionPublicCoverBuilder(builder);
    submitTrainModelForReviewBuilder(builder);
    getCollectionFoldersBuilder(builder);
    createCollectionFolderBuilder(builder);
    putCollectionToFolderBuilder(builder);
    deleteCollectionFolderBuilder(builder);
    editCollectionFolderBuilder(builder);
    generateAnonymizeBuilder(builder);
    getAnonymizedCollectionsBuilder(builder);
    selectCollectionsInCollectionGroupBuilder(builder);
    fetchCollectionBuilder(builder);
    builder.addCase(LOGOUT, (state) => ({
      ...initialState,
      categories: state.categories,
      exploreCollections: state.exploreCollections,
      loggedIn: false,
    }));
  },
});

export const {
  clearCollections,
  clearExploreCollections,
  resetCollectionsPagination,
  resetExploreCollectionsPagination,
  setSelectedCollection,
  clearTrainModels,
  setSelectedTrainModel,
  clearSelectedTrainModel,
  setSelectedExploreCollectionFilter,
  setSelectedExploreCollectionSearch,
  setSelectedExploreCollectionKeyword,
  setTrainingPage,
} = modelSlice.actions;

export const selectCategories = (state) => state.modelInfo.categories;
export const selectGenerateCollection = (state) =>
  state.modelInfo.generateCollection;

export const selectGenerateImageCollectionPending = (state) =>
  state.modelInfo.generateImageCollectionPending;

export const selectEnchantPromptPending = (state) =>
  state.modelInfo.enchantPromptPending;

export const selectCollections = (state) => state.modelInfo.collections;
export const selectFavorites = (state) => state.modelInfo.favoriteCollections;

export const selectFavoritesPage = (state) =>
  state.modelInfo.favoriteCollections.page;

export const selectToggleToFavoriteLoading = (state) =>
  state.modelInfo.toggleToFavoritePending;
export const selectExploreCollections = (state) =>
  state.modelInfo.exploreCollections;
export const selectExploreCollectionsFilter = (state) =>
  state.modelInfo.exploreCollections.filter;
export const selectExploreCollectionsSearch = (state) =>
  state.modelInfo.exploreCollections.search;
export const selectExploreCollectionsKeyword = (state) =>
  state.modelInfo.exploreCollections.keyword;

export const selectExploreCollectionsPage = (state) =>
  state.modelInfo.exploreCollections.page;

export const selectSelectedCollection = (state) =>
  state.modelInfo.selectedCollection;

export const selectDeleteCollectionPending = (state) =>
  state.modelInfo.deleteCollectionPending;
export const selectHideCollectionPending = (state) =>
  state.modelInfo.hideCollectionPending;
export const selectHideCollectionVipPending = (state) =>
  state.modelInfo.hideCollectionVipPending;
export const selectRateModelPending = (state) =>
  state.modelInfo.rateModelPending;

export const selectTrainModels = (state) => state.modelInfo.trainModels;
export const selectSelectedTrainModel = (state) =>
  state.modelInfo.selectedTrainModel;

export const selectCollectionDetails = (state) =>
  state.modelInfo.collectionDetails;

export const selectCreateTrainModelLoading = (state) =>
  state.modelInfo.createTrainModel.loading;
export const selectUpdateTrainModelLoading = (state) =>
  state.modelInfo.updateTrainModel.loading;

export const selectDeleteTrainModelPending = (state) =>
  state.modelInfo.deleteTrainModelPending;

export const selectTopCreators = (state) => state.modelInfo.topCreators;
export const selectGetTopCreatorsPending = (state) =>
  state.modelInfo.getTopCreatorsPending;

export const selectRegenerateCollectionPending = (state) =>
  state.modelInfo.regenerateCollectionPending;

export const selectRegenerateImageCollectionPending = (state) =>
  state.modelInfo.regenerateImageCollectionPending;

export const selectGalleryData = (state) => state.modelInfo.gallery.data;
export const selectGalleryDataPending = (state) =>
  state.modelInfo.getDefaultGalleryPending;

export const selectCoverForTrainModePending = (state) =>
  state.modelInfo.selectCoverForTrainModelPending;

export const selectSetCollectionPublicCoverPending = (state) =>
  state.modelInfo.setCollectionPublicCoverPending;

export const selectSubmitTrainModelForReviewPending = (state) =>
  state.modelInfo.submitTrainModelForReviewPending;

export const selectCollectionFolders = (state) =>
  state.modelInfo.collectionFolders;

export const selectCreateFolderPending = (state) =>
  state.modelInfo.createFolderPending;

export const selectDeleteFolderPending = (state) =>
  state.modelInfo.deleteFolderPending;

export const selectPutCollectionFolderPending = (state) =>
  state.modelInfo.putCollectionToFolderPending;

export const selectEditFolderPending = (state) =>
  state.modelInfo.editFolderPending;

export const selectGenerateAnonymizedCollectionPending = (state) =>
  state.modelInfo.generateAnonymizedCollectionPending;

export const selectGetAnonymizedCollectionPending = (state) =>
  state.modelInfo.anonymizedCollections.loading;

export const selectGetAnonymizedCollections = (state) =>
  state.modelInfo.anonymizedCollections.data;

export const selectCollectionsInCollectionGroupPending = (state) =>
  state.modelInfo.selectCollectionsInCollectionGroupPending;

export default modelSlice.reducer;
